import { SanityImageSource } from '@sanity/image-url/lib/types/types'

import { SanityImage } from 'src/components/atoms/SanityImage'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { ImageListRebrandResult } from 'src/components/layouts/Page/SanityContentPage/types'
import { Text, TextValue } from 'src/components/molecules/sanity/Content'

import { CmsCtaButtonRebrand } from '../CmsCtaButtonRebrand'

export type ImageListSectionRebrandProps = Omit<
  ImageListRebrandResult,
  'description' | 'image' | 'direction'
> & {
  description?: TextValue | string
  image?: SanityImageSource
  imagePosition?: 'left' | 'right'
}

const BACKGROUND_CLASS: Record<
  NonNullable<ImageListSectionRebrandProps['backgroundColor']>,
  string
> = {
  lavenderPale: 'bg-lavenderPale',
  limePale: 'bg-limePale',
  linen: 'bg-linen'
}

const ImageListSectionRebrand = ({
  heading,
  image,
  ctas,
  description,
  imagePosition = 'left',
  eyebrow,
  items,
  backgroundColor = 'linen',
  ctaPosition = 'right'
}: ImageListSectionRebrandProps) => {
  const imagePositionClass =
    imagePosition === 'right' ? 'lg:flex-row-reverse' : 'lg:flex-row'
  const mobileDirectionClass = image ? 'flex-col' : 'flex-col-reverse'

  const textColumnSide = imagePosition === 'left' ? 'right' : 'left'
  const ctasInTextColumn = ctaPosition === textColumnSide
  const hasItems = !!items && items.length > 0
  const itemsInTextColumn = !!image

  const ctaButtons =
    ctas && Array.isArray(ctas) ? (
      <div className='flex flex-row gap-2 mt-4'>
        {ctas.map((ctaButton, idx) => (
          <CmsCtaButtonRebrand key={`cta_${idx}`} {...ctaButton} />
        ))}
      </div>
    ) : (
      ctas
    )

  const itemsList = hasItems && (
    <ol className='flex flex-col gap-4'>
      {items!.map((item, idx) => {
        const titleOnly = !!item.title && !item.description
        return (
          <li
            key={`item_${idx}`}
            className={`flex flex-row gap-4 ${
              titleOnly ? 'items-center [&_h5]:mb-0 mb-6' : 'items-start'
            }`}
          >
            <span className='flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-limeBright font-body-rebrand font-extrabold text-lg leading-[160%] text-forestDeep'>
              {idx + 1}
            </span>
            <div className='flex flex-col gap-1'>
              {item.title && (
                <TypographyRebrand color='forestDeep' variant='h5'>
                  {item.title}
                </TypographyRebrand>
              )}
              {item.description && (
                <TypographyRebrand color='forestDeep' variant='introduction'>
                  {item.description}
                </TypographyRebrand>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )

  return (
    <div className={`py-6 lg:py-22 ${BACKGROUND_CLASS[backgroundColor]}`}>
      <div
        className={`container mx-auto flex ${mobileDirectionClass} lg:items-stretch gap-6 lg:gap-28 px-6 lg:px-20 ${imagePositionClass}`}
      >
        <div className='flex flex-col gap-6 w-full lg:flex-1 lg:self-stretch'>
          {image && (
            <div className='relative w-full aspect-[5/4] lg:grow overflow-hidden'>
              <SanityImage
                alt=''
                source={image}
                fill
                className='object-cover'
              />
            </div>
          )}
          {!itemsInTextColumn && hasItems && itemsList}
          {!ctasInTextColumn && ctaButtons}
        </div>
        <div
          className={`flex flex-col gap-1 lg:flex-1 lg:self-stretch ${
            image ? 'lg:justify-center' : ''
          }`}
        >
          {eyebrow && (
            <TypographyRebrand color='forestDeep' variant='eyebrow'>
              {eyebrow}
            </TypographyRebrand>
          )}
          {heading && (
            <TypographyRebrand color='forestDeep' variant='h2'>
              <Text
                components={{
                  block: ({ children }) => (
                    <span className='block'>{children}</span>
                  )
                }}
                value={heading}
              />
            </TypographyRebrand>
          )}
          {description && (
            <TypographyRebrand color='forestDeep' variant='introduction'>
              {typeof description === 'string' ? (
                description
              ) : (
                <Text
                  components={{
                    block: ({ children }) => (
                      <p className='mb-4 last:mb-0'>{children}</p>
                    )
                  }}
                  value={description}
                />
              )}
            </TypographyRebrand>
          )}
          {itemsInTextColumn && hasItems && itemsList}
          {ctasInTextColumn && ctaButtons}
        </div>
      </div>
    </div>
  )
}

export default ImageListSectionRebrand
